import styled from '@emotion/styled';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import * as qS from 'query-string';
import React from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { useTranslation } from '../../../../../i18next';
import { encodeObjBase64 } from '../../../../../services/base64';
import { callFirebaseFunction } from '../../../../../services/firebaseFunctions';

const toOAuthCodeUrl = (
  orgId: string,
  baseUrl: string,
  clientId: string,
  redirectUri: string,
  otherParams?: { [key: string]: string | number },
  otherStateParams: { [key: string]: string | number } = {}
) => {
  const state = encodeObjBase64({
    orgId,
    key: shortid(),
    ts: Date.now(),
    ...otherStateParams
  });
  const query = {
    ...otherParams,
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    state
  };
  return `${baseUrl}?${qS.stringify(query)}`;
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

export const OAuthSetupDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  orgId: string;
  name: string;
  oauthUrl: string;
  clientId: string;
  redirectUri: string;
  otherParams?: { [key: string]: string | number };
  otherStateParams?: { [key: string]: string | number };
  buttonDisabled?: boolean;
  customButton?: (href: string) => React.ReactNode;
}> = ({
  open,
  onClose,
  orgId,
  name,
  oauthUrl,
  clientId,
  redirectUri,
  otherParams,
  otherStateParams,
  children,
  buttonDisabled,
  customButton
}) => {
  const { t } = useTranslation();
  const href = toOAuthCodeUrl(
    orgId,
    oauthUrl,
    clientId,
    redirectUri,
    otherParams,
    otherStateParams
  );
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {name} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>{children}</Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            {customButton ? (
              customButton(href)
            ) : (
              <Button
                variant="contained"
                color="primary"
                href={href}
                target="_blank"
                disabled={buttonDisabled}
              >
                {t('org.integrations.actions.setup')}
              </Button>
            )}
          </>
        }
      />
    </Dialog>
  );
};

export const OAuthSetupDialogV2: React.FC<{
  open: boolean;
  onClose: () => void;
  orgId: string;
  name: string;
  oauthCf: string;
  buttonDisabled?: boolean;
  customButton?: (onClick: () => Promise<void>) => React.ReactNode;
}> = ({
  open,
  onClose,
  orgId,
  name,
  oauthCf,
  children,
  buttonDisabled,
  customButton
}) => {
  const { t } = useTranslation();
  const onClick = async () => {
    const { url } = await callFirebaseFunction(oauthCf, { orgId });
    window.open(url, '__blank');
  };
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {name} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>{children}</Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            {customButton ? (
              customButton(onClick)
            ) : (
              <ButtonWithPromise
                variant="contained"
                color="primary"
                onClick={onClick}
                disabled={buttonDisabled}
                pending={`${t('org.integrations.actions.redirecting')}...`}
              >
                {t('org.integrations.actions.setup')}
              </ButtonWithPromise>
            )}
          </>
        }
      />
    </Dialog>
  );
};
